import { Avatar, Body, Heading, Icon } from '@assembly-js/design-system';
import {
  Button,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  useTheme,
} from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import React from 'react';
import { shallowEqual } from 'react-redux';

import { NotificationBadge } from 'src/components/NotificationBadge';
import { Company } from 'src/constants';
import { PortalConfigContext, SidebarTheme } from 'src/context';
import { InboxNotificationDetailsResponse } from 'src/entities/Notifications';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import history from 'src/history';
import { useLogout } from 'src/hooks/useLogout';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { ApiTags } from 'src/services/api/appApi';
import { applicationSettingsApi } from 'src/services/api/applicationsApi';
import { contractsApi } from 'src/services/api/contractsApi';
import { formsApi } from 'src/services/api/formsApi';
import { useGetInboxNotificationsQuery } from 'src/services/api/inboxApi';
import { invoicesApi } from 'src/services/api/invoicesApi';
import { usersApi } from 'src/services/api/usersApi';
import { setActiveCompanyId } from 'src/store/activeCompany/slice';
import { selectAllCompaniesMap } from 'src/store/clients/types';
import { listArticles } from 'src/store/knowledgeBase/actions';
import { RootState } from 'src/store/reduxTypes';
import { selectCurrentUser } from 'src/store/storeUtils';
import { isUnreadNotification } from 'src/utils/NotificationsUtils';

const selectCompanyUnreadNotifications = createSelector(
  [
    (state: RootState) => state.notifications.fileNotifications,
    (state: RootState) => state.notifications.newInvoiceNotifications,
    (state: RootState) => state.notifications.formRequestNotifications,
    (state: RootState) => state.notifications.esigRequestNotifications,
    (
      _state: RootState,
      inboxNotifications: InboxNotificationDetailsResponse[] | undefined,
    ) => inboxNotifications,
  ],

  (
    fileNotifications,
    newInvoiceNotifications,
    formRequestNotifications,
    esigRequestNotifications,
    inboxNotifications,
  ) => {
    const companyToUnreadCount = new Map<string, number>();

    // Combine all notification types
    const allNotifications = [
      ...fileNotifications,
      ...newInvoiceNotifications,
      ...formRequestNotifications,
      ...esigRequestNotifications,
    ];

    const customAppUnreadNotifications =
      inboxNotifications?.filter((notification) => {
        return (
          notification.event === 'customApp.action' && !notification.isRead
        );
      }) || [];

    for (const notification of customAppUnreadNotifications) {
      const { companyId } = notification;
      if (!companyId) continue; // Skip if companyId is missing
      companyToUnreadCount.set(
        companyId,
        (companyToUnreadCount.get(companyId) || 0) + 1,
      );
    }

    // Filter only unread notifications
    const unreadNotifications = allNotifications.filter((notification) =>
      isUnreadNotification(notification.ownerId),
    );

    // Group unread notifications by companyId
    for (const notification of unreadNotifications) {
      const { companyId } = notification;
      if (!companyId) continue; // Skip if companyId is missing
      companyToUnreadCount.set(
        companyId,
        (companyToUnreadCount.get(companyId) || 0) + 1,
      );
    }

    return companyToUnreadCount;
  },
);

function getCompanyInitials(name: string) {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

const useCompanySwitcher = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { SignoutRedirectURL: signoutRedirectURL } =
    React.useContext(PortalConfigContext);
  const companySwitcherVisibility = useCompanySwitcherVisibility();

  const { activeClientCompanyId, isClient } = useAppSelector(
    (state) => ({
      activeClientCompanyId: state.activeCompany.activeCompanyId,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );

  // Get current user and companies map from Redux store
  const currentUser = useAppSelector(selectCurrentUser);
  const companiesMap = useAppSelector(selectAllCompaniesMap);

  // Get user companies using efficient map lookup
  const userCompanies = React.useMemo(() => {
    if (!currentUser || !isClient || !currentUser.fields.companyIds) {
      return [];
    }

    return currentUser.fields.companyIds
      .map((companyId) => companiesMap.get(companyId))
      .filter((company): company is Company =>
        Boolean(company && company.fields.name),
      )
      .sort((a, b) => {
        const nameA = a.fields.name.trim().toLowerCase();
        const nameB = b.fields.name.trim().toLowerCase();
        const nameComparison = nameA.localeCompare(nameB);

        // If names are the same, use company ID for deterministic ordering
        if (nameComparison === 0) {
          return a.id.localeCompare(b.id);
        }

        return nameComparison;
      });
  }, [currentUser, isClient, companiesMap]);

  // Get inbox notifications data
  const { data: inboxNotifications } = useGetInboxNotificationsQuery();

  const companyToUnreadNotifications = useAppSelector((state) =>
    selectCompanyUnreadNotifications(state, inboxNotifications),
  );

  const companyToUnreadMessages = useAppSelector(
    (state) => state.messages.totalUnreadMessagesByCompany,
  );

  const getCompanyUnreadNotificationsCount = React.useCallback(
    (companyId: string) => {
      const unreadAppsNotifications =
        companyToUnreadNotifications.get(companyId) ?? 0;
      const unreadMessages = companyToUnreadMessages?.[companyId] ?? 0;

      return unreadAppsNotifications + unreadMessages;
    },
    [companyToUnreadNotifications, companyToUnreadMessages],
  );

  const handleLogout = useLogout();
  const handleClick = (company: Company) => () => {
    dispatch(setActiveCompanyId(company.id));
    history.push('/');
  };

  React.useEffect(() => {
    if (userCompanies.length > 0) {
      const localActiveCompanyId = localStorage.getItem('activeCompanyId');
      const isInvalidLocalActiveCompanyId =
        localActiveCompanyId &&
        !userCompanies.some((c) => c.id === localActiveCompanyId);
      if (isInvalidLocalActiveCompanyId) {
        // This block handles various scenarios that result in the localStorage
        // value being omitted from the user's companies list:
        // 1. Internal users copying magic links and signing in as many clients.
        // 2. Clients being removed from a company between sessions.
        localStorage.removeItem('activeCompanyId');
        dispatch(setActiveCompanyId(userCompanies.at(0)?.id ?? ''));
      } else if (!activeClientCompanyId) {
        const activeCompanyId =
          localActiveCompanyId ?? userCompanies.at(0)?.id ?? '';
        dispatch(setActiveCompanyId(activeCompanyId));
      }
    }
  }, [userCompanies, activeClientCompanyId]);

  React.useEffect(() => {
    if (!isClient) {
      return;
    }
    if (activeClientCompanyId) {
      localStorage.setItem('activeCompanyId', activeClientCompanyId);
    }
  }, [activeClientCompanyId, isClient]);

  React.useEffect(() => {
    dispatch(applicationSettingsApi.util.invalidateTags([ApiTags.apps]));
    dispatch(usersApi.util.invalidateTags([ApiTags.users]));
    dispatch(contractsApi.util.invalidateTags([ApiTags.contracts]));
    dispatch(invoicesApi.util.invalidateTags([ApiTags.invoices]));
    dispatch(formsApi.util.invalidateTags([ApiTags.form_responses]));
    dispatch(listArticles());
    dispatch(usersApi.util.invalidateTags([ApiTags.otherUserData]));
  }, [dispatch, activeClientCompanyId]);

  const selectedCompany = React.useMemo(() => {
    return userCompanies?.find(
      (company) => company.id === activeClientCompanyId,
    );
  }, [userCompanies, activeClientCompanyId]);

  return {
    companies: userCompanies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    theme,
    selectedCompany,
    signoutRedirectURL,
    getCompanyUnreadNotificationsCount,
  } as const;
};

export const DesktopCompanySwitcher = ({ theme }: { theme: SidebarTheme }) => {
  const {
    companies,
    companySwitcherVisibility,
    handleClick,
    selectedCompany,
    getCompanyUnreadNotificationsCount,
  } = useCompanySwitcher();
  const { backgroundColor, textColor } = useNotificationBadgeData(true); // isSidebar = true
  const [hoverCompanyId, setHoverCompanyId] = React.useState<string | null>(
    null,
  );
  const [clampedCompanies, setClampedCompanies] = React.useState<Set<string>>(
    new Set(),
  );
  const textRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

  // Check for text clamping
  React.useEffect(() => {
    const checkClamping = () => {
      const newClampedCompanies = new Set<string>();

      // Clear stale refs first
      const currentCompanyIds = new Set(companies.map((c) => c.id));
      textRefs.current.forEach((_, companyId) => {
        if (!currentCompanyIds.has(companyId)) {
          textRefs.current.delete(companyId);
        }
      });

      textRefs.current.forEach((element, companyId) => {
        if (element) {
          // Get the Body component's rendered element (should be a <p> tag)
          const bodyElement =
            element.querySelector('p') || element.firstElementChild;

          if (bodyElement && bodyElement instanceof HTMLElement) {
            const { scrollHeight } = bodyElement;
            const { offsetHeight } = bodyElement;
            const isClamped = scrollHeight > offsetHeight;

            if (isClamped) {
              newClampedCompanies.add(companyId);
            }
          }
        }
      });

      setClampedCompanies(newClampedCompanies);
    };

    // Use window resize event instead of ResizeObserver
    const handleResize = () => checkClamping();

    // Check immediately
    checkClamping();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [companies]);

  const setTextRef = React.useCallback(
    (companyId: string) => (element: HTMLDivElement | null) => {
      if (element) {
        textRefs.current.set(companyId, element);
      } else {
        textRefs.current.delete(companyId);
      }
    },
    [],
  );

  const getAvatarStyle = React.useCallback(
    (c: Company) => {
      const isSelected = selectedCompany?.id === c.id;
      const isHovered = hoverCompanyId === c.id;
      return {
        border: 'none',
        marginBottom: -12,
        outline: '2px solid',
        outlineColor: isSelected
          ? theme.sidebarTextColor
          : isHovered
            ? 'gray'
            : 'transparent',
        outlineOffset: 2,
      };
    },
    [selectedCompany, hoverCompanyId, theme],
  );

  if (companySwitcherVisibility.visibility !== 'desktop') {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-4 py-3 overflow-y-auto h-full">
      {companies.map((company) => {
        const isTextClamped = clampedCompanies.has(company.id);
        const buttonContent = (
          <button
            className="bg-transparent border-none shadow-none cursor-pointer relative flex flex-col items-center gap-5 w-[60px]"
            onClick={handleClick(company)}
          >
            <div className="relative">
              <Avatar
                alt={`${company.fields.name} logo`}
                onMouseOver={() => setHoverCompanyId(company.id)}
                onMouseLeave={() => setHoverCompanyId(null)}
                style={getAvatarStyle(company)}
                fallbackColor={company.fields.fallbackColor}
                size="md"
                src={company.fields.avatarImageURL || ''}
                text={getCompanyInitials(company.fields.name)}
                variant="rounded"
              />

              {getCompanyUnreadNotificationsCount(company.id) > 0 ? (
                <div className="absolute top-[-5px] right-[-5px]">
                  <NotificationBadge
                    showZero={false}
                    isSidebar
                    content={getCompanyUnreadNotificationsCount(company.id)}
                    backgroundColor={backgroundColor}
                    textColor={textColor}
                  />
                </div>
              ) : null}
            </div>
            <div
              className="flex flex-1"
              ref={setTextRef(company.id)}
              style={{ color: theme.sidebarTextColor }}
            >
              <Body className="text-[10px] line-clamp-2 text-center ml-[-8px] mr-[-8px] flex-1">
                {company.fields.name}
              </Body>
            </div>
          </button>
        );

        return isTextClamped ? (
          <Tooltip
            key={company.id}
            title={company.fields.name}
            placement="right"
            enterDelay={300}
          >
            {buttonContent}
          </Tooltip>
        ) : (
          <div key={company.id}>{buttonContent}</div>
        );
      })}
    </div>
  );
};

export const MobileCompanySwitcher = () => {
  const {
    companies,
    companySwitcherVisibility,
    handleClick,
    handleLogout,
    theme,
    selectedCompany,
    signoutRedirectURL,
    getCompanyUnreadNotificationsCount,
  } = useCompanySwitcher();
  const { backgroundColor, textColor } = useNotificationBadgeData(true); // isSidebar = true
  const [open, setOpen] = React.useState(false);
  const anchorEl = React.useRef<HTMLButtonElement>(null);

  if (companySwitcherVisibility.visibility !== 'mobile') {
    return null;
  }

  return (
    <div>
      <Button
        className="bg-transparent border-none shadow-none w-full flex items-center justify-start px-2 py-2"
        ref={anchorEl}
        onClick={() => setOpen(true)}
        style={{
          // TODO: remove this once we integrate the theme in tailwind
          backgroundColor: theme.palette.primary.dark,
        }}
      >
        {selectedCompany && (
          <>
            <Avatar
              className="mr-2 block border-none"
              src={selectedCompany?.fields.avatarImageURL || ''}
              size="sm"
              fallbackColor={selectedCompany?.fields.fallbackColor}
              alt={selectedCompany?.fields.name || ''}
              variant="rounded"
              text={selectedCompany.fields.name.slice(0, 1)}
            />
            <Body className="text-white truncate" size="sm">
              {selectedCompany?.fields.name}
            </Body>
            <Icon
              icon={open ? 'ChevronUp' : 'ChevronDown'}
              className={'w-[12px] h-[12px] ml-[6px] text-white'}
            />
          </>
        )}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        classes={{
          paper: 'w-[240px] mt-[2px] ml-[-8px] shadow-dropdown-menu',
        }}
      >
        <MenuList className="p-0">
          {companies.map((company) => (
            <MenuItem
              key={company.id}
              onClick={handleClick(company)}
              className="h-8 p-3 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Avatar
                  src={company.fields.avatarImageURL || ''}
                  size="sm"
                  fallbackColor={company.fields.fallbackColor}
                  alt={company.fields.name}
                  variant="rounded"
                  text={company.fields.name.slice(0, 1)}
                />
                <Heading className="text-[14px]">{company.fields.name}</Heading>
              </div>
              {getCompanyUnreadNotificationsCount(company.id) > 0 ? (
                <NotificationBadge
                  showZero={false}
                  isSidebar
                  content={getCompanyUnreadNotificationsCount(company.id)}
                  backgroundColor={backgroundColor}
                  textColor={textColor}
                />
              ) : null}
            </MenuItem>
          ))}
          <div className="h-[1px] bg-secondary-hover" />
          <MenuItem
            className="h-8 p-3"
            onClick={() => handleLogout(false, signoutRedirectURL, true)}
          >
            <Body className="text-[14px]">Log out</Body>
          </MenuItem>
        </MenuList>
      </Popover>
    </div>
  );
};
